/**
 * First-party usage events for the admin insights dashboard.
 *
 * Only what the server can't see for itself goes through here: planner
 * searches with the result count the visitor saw, campsite views, adds to the
 * Shopify cart, and share-sheet use. Trips, stops, saves and logins are
 * recorded server-side where they happen.
 *
 * Identity: the signed-in user's own token (verified server-side), plus a
 * random per-tab session id so a signed-out search can be tied to the same
 * person if they sign in later in that visit. No email, no IP, no
 * fingerprinting. Fire-and-forget: never throws, never blocks the UI.
 */
import { getUserToken } from '../utils/reviewerApi';

const ENDPOINT = 'https://app.trip-planner.com.au/api/activity/track';
const SID_KEY = 'ca_activity_sid';

export type ActivityType = 'search' | 'route_search' | 'campsite_view' | 'cart_add' | 'trip_shared';

export interface ActivityProps {
  term?: string;
  resultCount?: number;
  entityId?: number | string;
  value?: number | null;
  qty?: number;
  source?: string;
  name?: string;
  tripId?: string;
  meta?: Record<string, unknown>;
}

export function sessionId(): string | null {
  try {
    let sid = window.sessionStorage.getItem(SID_KEY);
    if (!sid) {
      sid = (crypto?.randomUUID?.() || `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`);
      window.sessionStorage.setItem(SID_KEY, sid);
    }
    return sid;
  } catch {
    return null;
  }
}

/** Where on the site an event happened, from the path (works for both the
 *  app-proxy prefix and the bare domain). */
export function surfaceFromPath(pathname: string, search = ''): string {
  const p = pathname.replace(/^\/apps\/campsite-explorer/, '') || '/';
  if (p.startsWith('/trip/')) return 'trip';
  if (p.startsWith('/camp-sites/')) return 'campsite';
  if (p.startsWith('/regions')) return 'region';
  if (p === '/' || p === '') return /[?&]plan=1/.test(search) ? 'planner' : 'explorer';
  return p.split('/')[1] || 'other';
}

/** Body sent to the intake. Exported for tests. */
export function buildPayload(type: ActivityType, props: ActivityProps, sid: string | null, source: string) {
  const body: Record<string, unknown> = { type, sid, source: props.source || source };
  if (props.term !== undefined) body.term = String(props.term).slice(0, 200);
  if (props.resultCount !== undefined) body.resultCount = Math.max(0, Math.round(Number(props.resultCount) || 0));
  if (props.entityId !== undefined && props.entityId !== null && `${props.entityId}` !== '') {
    const n = Number(props.entityId);
    if (Number.isFinite(n)) body.entityId = n;
  }
  if (typeof props.value === 'number' && Number.isFinite(props.value)) body.value = props.value;
  if (props.qty) body.qty = props.qty;
  if (props.name) body.name = String(props.name).slice(0, 200);
  if (props.tripId) body.tripId = props.tripId;
  if (props.meta) body.meta = props.meta;
  return body;
}

/** The explorer runs a search twice on some paths (URL-param load, and the
 *  location + name searches both settling), which logged every planner search
 *  as a pair. An identical event inside this window is the same action. */
export const DEDUPE_WINDOW_MS = 4000;
const recent = new Map<string, number>();

export function isDuplicate(type: ActivityType, props: ActivityProps, now: number): boolean {
  const key = JSON.stringify([type, props.term ?? null, props.resultCount ?? null,
    props.entityId ?? null, props.name ?? null, props.value ?? null]);
  const last = recent.get(key);
  recent.set(key, now);
  if (recent.size > 200) {
    recent.forEach((t, k) => { if (now - t > DEDUPE_WINDOW_MS) recent.delete(k); });
  }
  // cart adds are deliberate repeats (two of the same item), never collapse them
  return type !== 'cart_add' && last !== undefined && now - last < DEDUPE_WINDOW_MS;
}

export function trackActivity(type: ActivityType, props: ActivityProps = {}): void {
  if (typeof window === 'undefined') return;
  try {
    if (isDuplicate(type, props, Date.now())) return;
    const sid = sessionId();
    const body = buildPayload(type, props, sid, surfaceFromPath(window.location.pathname, window.location.search));
    const token = getUserToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    sendWithRetry(JSON.stringify(body), headers);
  } catch {
    /* analytics must never break the page */
  }
}

/** Delays before each retry. Live, the events fired during the explorer's
 *  initial burst of searches came back 503 while the same payload a few
 *  seconds later was accepted, so a transient failure gets two more tries. */
export const RETRY_DELAYS_MS = [1500, 4000];

export function sendWithRetry(body: string, headers: Record<string, string>, attempt = 0): void {
  const retry = () => {
    if (attempt < RETRY_DELAYS_MS.length) {
      setTimeout(() => sendWithRetry(body, headers, attempt + 1), RETRY_DELAYS_MS[attempt]);
    }
  };
  // keepalive lets the request finish if the click navigates away (cart
  // add → cart drawer, search → result click).
  fetch(ENDPOINT, { method: 'POST', headers, body, keepalive: true, mode: 'cors' })
    .then((res) => {
      if (res.status >= 500) retry();
    })
    .catch(retry);
}
